/**
 * SOLA Chat Logger
 *
 * Simplified structured logging for debugging chat state and performance.
 * Designed for development debugging with minimal overhead.
 *
 * Categories:
 * - state: State changes and updates
 * - perf: Performance measurements
 * - sync: Cross-tab and server synchronization
 * - hydration: IndexedDB hydration events
 * - error: Error tracking
 * - lifecycle: Component lifecycle events
 * - user: User interactions
 */

type LogCategory = 'state' | 'perf' | 'sync' | 'hydration' | 'error' | 'lifecycle' | 'user'

class SOLALogger {
  private enabled: boolean
  private timers: Map<string, number>

  constructor() {
    // Enable in development or when NEXT_PUBLIC_ENABLE_SOLA_LOGS=true
    this.enabled =
      process.env.NODE_ENV === 'development' ||
      process.env.NEXT_PUBLIC_ENABLE_SOLA_LOGS === 'true'
    this.timers = new Map()
  }

  state(message: string, data?: any) {
    this.log('state', message, data)
  }

  perf(message: string, data?: any) {
    this.log('perf', message, data)
  }

  sync(message: string, data?: any) {
    this.log('sync', message, data)
  }

  hydration(message: string, data?: any) {
    this.log('hydration', message, data)
  }

  error(message: string, error?: Error, data?: any) {
    const errorData = {
      ...data,
      error: error ? {
        name: error.name,
        message: error.message,
        stack: error.stack,
      } : undefined,
    }
    console.error(`[SOLA:error] ${message}`, errorData)
  }

  lifecycle(message: string, data?: any) {
    this.log('lifecycle', message, data)
  }

  user(message: string, data?: any) {
    this.log('user', message, data)
  }

  // Performance timing methods
  time(label: string) {
    if (!this.enabled) return
    this.timers.set(label, performance.now())
  }

  timeEnd(label: string, data?: any) {
    if (!this.enabled) return
    const startTime = this.timers.get(label)
    if (startTime === undefined) {
      console.warn(`[SOLA:perf] Timer '${label}' does not exist`)
      return
    }
    const duration = performance.now() - startTime
    this.timers.delete(label)
    this.perf(`${label} completed in ${duration.toFixed(2)}ms`, data)
  }

  private log(category: LogCategory, message: string, data?: any) {
    if (!this.enabled) return

    const colors: Record<LogCategory, string> = {
      state: '#3b82f6',
      perf: '#10b981',
      sync: '#8b5cf6',
      hydration: '#f59e0b',
      error: '#ef4444',
      lifecycle: '#6b7280',
      user: '#06b6d4',
    }

    console.log(
      `%c[SOLA:${category}] ${message}`,
      `color: ${colors[category]}; font-weight: bold`,
      data || ''
    )
  }
}

// Export singleton instance
export const logger = new SOLALogger()

// Add to window for easy access in console (development only)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).solaLogger = logger
}
